import { Check } from 'lucide-react';
import { useFrappeFileUrl } from '@/lib/utils/frappeUrl';
import { cn } from '@/lib/utils';
import { formatCurrency } from './formatCurrency';
import type { POSItem } from './types';

interface POSTableViewProps {
  items: POSItem[];
  selectedItemCodes: string[];
  onSelectItem: (item: POSItem) => void;
  currency?: string;
}

/**
 * Table view for POS picker - shows products in a list format
 */
export function POSTableView({ items, selectedItemCodes, onSelectItem, currency = 'THB' }: POSTableViewProps) {
  const toAbsolute = useFrappeFileUrl();

  if (items.length === 0) {
    return null; // Parent handles empty state
  }

  return (
    <div className="space-y-2">
      {items.map((item) => {
        const itemCode = item.item_code || '';
        const isSelected = selectedItemCodes.includes(itemCode);
        const rawImage = item.primary_image || item.image;
        const imageUrl = rawImage ? toAbsolute(rawImage) : null;

        return (
          <div
            key={itemCode || item.name}
            className={cn(
              'flex items-center gap-4 p-3 rounded-sm border cursor-pointer hover:bg-muted/50 transition-colors',
              isSelected && 'bg-primary/5 border-primary/30'
            )}
            onClick={() => onSelectItem(item)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectItem(item);
              }
            }}
            tabIndex={0}
            role="button"
          >
            {/* Thumbnail */}
            <div className="w-12 h-12 rounded-xs overflow-hidden bg-muted flex-shrink-0 flex items-center justify-center">
              {imageUrl ? (
                <img src={imageUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-muted-foreground text-lg font-medium">
                  {(item.item_name || item.item_code || '?')[0].toUpperCase()}
                </span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{item.item_name}</div>
              <div className="text-sm text-muted-foreground truncate">{item.item_code}</div>
            </div>

            {/* Price */}
            <div className="text-sm font-medium">
              {item.standard_rate && Number(item.standard_rate) > 0
                ? formatCurrency(Number(item.standard_rate), currency)
                : '—'}
            </div>

            {/* Selected indicator */}
            {isSelected && (
              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-primary-foreground" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
